import { Component, TemplateFunction } from '@ribajs/core'
import { SbCollectionComponent } from './index.js'
import { ShopifyProductService } from '../services/index.js'
import { Pjax } from '@ribajs/router'
import { redirectToPath } from '../utils/index.js'

const ACTIVE_CLASS = 'active'

interface Scope {
  // OPTIONS

  /** The collection handle */
  handle?: string
  /** The parent collection handle */
  mainHandle?: string
  /** If true the collection will be loaded dynamic without a page reload, otherwise */
  dynamic: boolean

  // METHODS

  load: SbSubCollectionComponent['load']
}

export class SbSubCollectionComponent extends Component {
  public static tagName = 'sb-sub-collection'
  protected collectionEl?: SbCollectionComponent

  static get observedAttributes(): string[] {
    return ['handle', 'main-handle', 'dynamic']
  }

  protected requiredAttributes(): string[] {
    return ['handle']
  }

  protected collectionAnchorEls: HTMLAnchorElement[] = []

  public loadedPages: number[] = []

  public scope: Scope = {
    handle: undefined,
    mainHandle: '',
    dynamic: true,
    load: this.load,
  }

  /** Go the collection by new page request (the old school way) */
  public goToCollection(event?: Event, url?: string | URL) {
    url ||= (event?.target as HTMLAnchorElement | null)?.href

    if (!url && this.scope.handle) {
      url = ShopifyProductService.getCollectionUrl(this.scope.handle, 1)
    }
    if (url) {
      const href = url.toString()
      if (window.location.href === href) {
        return
      }

      event?.preventDefault()
      event?.stopPropagation()

      if (window.router_enable) {
        Pjax.getInstance()?.goTo(href)
      } else {
        redirectToPath(href)
      }
    }
  }

  protected setInactive() {
    this.classList.remove(ACTIVE_CLASS)
  }

  /**
   * Get all Collections cards to remove the active class
   */
  protected setInactiveAll() {
    const collectionCards =
      Array.from(
        document.getElementsByTagName(
          this.tagName,
        ) as HTMLCollectionOf<SbSubCollectionComponent>,
      ) || undefined
    for (const collectionCard of collectionCards) {
      collectionCard.setInactive()
    }
  }

  protected setActive() {
    this.setInactiveAll()
    this.classList.add(ACTIVE_CLASS)
  }

  protected isActive() {
    return this.classList.contains(ACTIVE_CLASS)
  }

  /**
   * Dynamically load the collection without a new page request (the PWA way)
   * If dynamic is disabled `goToCollection` is used instead
   **/
  public async load(event?: Event, updateUrl = true) {
    if (this.isActive()) {
      console.warn(
        '[SbSubCollectionComponent] This collection is already active, do nothing',
      )
      event?.preventDefault()
      event?.stopPropagation()
      return
    }

    if (!this.scope.dynamic) {
      this.setActive()
      return this.goToCollection(event)
    }

    if (!this.collectionEl) {
      console.error(
        `[SbSubCollectionComponent] No collection element "${SbCollectionComponent.tagName}" found!`,
      )
      // Fallback
      this.goToCollection(event)
      return
    }
    if (!this.scope.handle) {
      this.error(`The handle attribute is required!`)
      return
    }

    this.setActive()
    // Dynamic load
    this.collectionEl.load(event, this.scope.handle, updateUrl)
  }

  constructor() {
    super()
    this.onNavigationLinkClick = this.onNavigationLinkClick.bind(this)
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbSubCollectionComponent.observedAttributes)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeNavigationLinkEventListeners()
  }

  /**
   * Get all collection navigation links to this current collection
   * @param onlyActiveLinks If true only the active links will be returned
   */
  protected getMainCollectionNavigationAnchors(onlyActiveLinks = false) {
    const selector = onlyActiveLinks
      ? `a.active[href$="/collections/${this.scope.handle}"]`
      : `a[href$="/collections/${this.scope.handle}"]`
    const collectionAnchorEls: HTMLAnchorElement[] = Array.from(
      document.querySelectorAll(selector),
    )
    return collectionAnchorEls
  }

  protected onNavigationLinkClick(event: Event) {
    if (!this.scope.handle) {
      throw new Error('The handle attribute is required!')
    }
    this.load(event, true)
    event.preventDefault()
    event.stopPropagation()
  }

  protected addNavigationLinkEventListeners() {
    // Remove old event listeners
    this.removeNavigationLinkEventListeners()

    this.collectionAnchorEls = this.getMainCollectionNavigationAnchors(false)
    for (const collectionAnchorEl of this.collectionAnchorEls) {
      collectionAnchorEl.addEventListener('click', this.onNavigationLinkClick)
    }
  }

  protected removeNavigationLinkEventListeners() {
    for (const collectionAnchorEl of this.collectionAnchorEls) {
      collectionAnchorEl.removeEventListener(
        'click',
        this.onNavigationLinkClick,
      )
    }
  }

  protected async afterBind() {
    await super.afterBind()
    this.setCollectionEl()
    // WORKAROUND To load the main collection if the user goes to the collection page over the navigation
    if (this.scope.mainHandle && this.scope.mainHandle === this.scope.handle) {
      this.addNavigationLinkEventListeners()
    }
    this.classList.add(`${SbSubCollectionComponent.tagName}-ready`)
  }

  protected async afterAllBind() {
    this.setCollectionEl()
    await super.afterAllBind()
  }

  protected setCollectionEl() {
    if (!this.collectionEl) {
      this.collectionEl =
        document.querySelector(
          `${SbCollectionComponent.tagName}[handle=${this.scope.mainHandle}]`,
        ) ||
        document.querySelector(`${SbCollectionComponent.tagName}`) ||
        undefined
    }

    // Fallback
    if (!this.collectionEl) {
      this.collectionEl =
        document.querySelector(
          `${SbCollectionComponent.tagName}[handle=${this.scope.handle}]`,
        ) ||
        document.querySelector(`${SbCollectionComponent.tagName}`) ||
        undefined
    }

    // Fallback 2
    if (!this.collectionEl) {
      this.collectionEl =
        document.querySelector(`${SbCollectionComponent.tagName}`) || undefined
    }
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
